<template>
  <div
    class="flex min-h-0 w-full flex-col py-6 text-gray-900 dark:text-gray-100"
    style="font-family: 'Inter', sans-serif; min-height: calc(100vh - 140px);"
  >
    <header class="mb-8">
 
      <h1 class="mt-3 text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
        MetalNx’s Physics-AI Digital Platforms for Metals
      </h1>
      <p class="mt-2 whitespace-nowrap text-sm leading-7 text-gray-600 dark:text-gray-400">
        Select a tool to explore its capabilities. Contact the MetalNx team to request access to additional modules.
      </p>
    </header>

    <div class="home-tools-grid">
      <router-link
        v-for="tool in tools"
        :key="tool.id"
        :to="tool.to"
        class="home-tool-card group flex flex-col rounded-2xl border border-gray-200 bg-white no-underline shadow-sm transition-all duration-200 dark:border-white/10 dark:bg-white/[0.03] dark:shadow-none"
        :class="`home-tool-card--span-${tool.row}`"
      >
        <div class="home-tool-card__icon">
          <Icon :id="tool.icon" size="1.5rem" color="#ffffff" />
        </div>

        <div class="mt-5 min-w-0 flex-1">
          <p class="text-[0.6875rem] font-semibold uppercase tracking-widest text-[color:var(--v-primary-base)]">
            {{ tool.subtitle }}
          </p>
          <h2 class="mt-1.5 text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            {{ tool.name }}
          </h2>
          <p class="mt-2 text-sm leading-6 text-gray-500 dark:text-gray-400">
            {{ tool.description }}
          </p>
        </div>

        <div class="mt-5 flex items-center gap-1.5 text-sm font-semibold text-[color:var(--v-primary-base)] opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          Open
          <svg class="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </div>
      </router-link>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue'
import { DIGITAL_TOOLS } from '@/constants/digitalTools'

export default {
  name: 'Home',
  components: { Icon },
  computed: {
    tools() {
      return DIGITAL_TOOLS
    },
  },
}
</script>

<style scoped>
.home-tools-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 1.25rem;
}

.home-tool-card {
  position: relative;
  min-height: 11rem;
  padding: 1.5rem;
  overflow: hidden;
}

@media (min-width: 640px) {
  .home-tool-card {
    padding: 1.75rem;
  }
}

.home-tool-card::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: radial-gradient(
    ellipse farthest-corner at 100% 100%,
    color-mix(in srgb, var(--v-primary-base) 40%, transparent) 0%,
    color-mix(in srgb, var(--v-primary-base) 16%, transparent) 42%,
    transparent 72%
  );
  opacity: 0.2;
  pointer-events: none;
  transition: opacity 0.22s ease;
}

.home-tool-card:hover::after {
  opacity: 0.32;
}

html.dark .home-tool-card::after {
  opacity: 0.16;
}

html.dark .home-tool-card:hover::after {
  opacity: 0.28;
}

.home-tool-card > * {
  position: relative;
  z-index: 1;
}

.home-tool-card--span-4,
.home-tool-card--span-3,
.home-tool-card--span-2 {
  grid-column: span 12;
}

@media (min-width: 640px) {
  .home-tool-card--span-4,
  .home-tool-card--span-3 {
    grid-column: span 6;
  }

  .home-tool-card--span-2 {
    grid-column: span 12;
  }
}

@media (min-width: 1024px) {
  .home-tool-card--span-4 {
    grid-column: span 3;
  }

  .home-tool-card--span-3 {
    grid-column: span 4;
  }

  .home-tool-card--span-2 {
    grid-column: span 6;
  }
}

.home-tool-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  color: #fff;
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--v-primary-base) 88%, white),
    var(--v-primary-base)
  );
  box-shadow: 0 4px 12px color-mix(in srgb, var(--v-primary-base) 28%, transparent);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.home-tool-card:hover {
  transform: translateY(-3px);
  border-color: color-mix(in srgb, var(--v-primary-base) 40%, rgb(229 231 235));
  box-shadow: 0 10px 28px rgb(0 0 0 / 0.08);
}

html.dark .home-tool-card:hover {
  border-color: color-mix(in srgb, var(--v-primary-base) 45%, rgb(255 255 255 / 0.1));
  box-shadow: 0 10px 28px rgb(0 0 0 / 0.4);
}

.home-tool-card:hover .home-tool-card__icon {
  transform: scale(1.05);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--v-primary-base) 40%, transparent);
}

.home-tool-card:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--v-primary-base) 55%, transparent);
  outline-offset: 2px;
}

.home-tool-card__icon ::v-deep(svg.icon) {
  fill: currentColor;
  color: #fff;
}
</style>
